import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { showToast } from "../helpers/toast"
import { fetchWonData, showWonModal } from "../helpers/won_modal"

export default class extends Controller {
  static targets = ["column"]
  static values = {
    // Prefixe URL pour change_stage. Permet de switcher entre B2B ("/deals") et B2C ("/deals-b2c").
    resourcePath: { type: String, default: "/deals" }
  }

  connect() {
    this.initSortable()
  }

  disconnect() {
    this.sortables?.forEach(s => s.destroy())
  }

  initSortable() {
    this.sortables = this.columnTargets.map(col =>
      Sortable.create(col, {
        group: "deals",
        animation: 150,
        ghostClass: "",
        dragClass: "shadow-xl",
        filter: "button",
        // ignore: "" override le defaut "a, img" de SortableJS qui empeche
        // tout drag sur les <a> — or nos cartes sont des link_to (donc <a>).
        ignore: "",
        // Drag HTML5 natif (fluide, GPU) + override du ghost-URL navigateur
        // par un clone de la carte via setDragImage. Evite forceFallback et son lag.
        setData: (dataTransfer, dragEl) => {
          dataTransfer.setData("Text", dragEl.textContent)
          dataTransfer.setDragImage(dragEl, 20, 20)
        },
        // Zone de drop généreuse pour les colonnes vides (défaut = 5px).
        emptyInsertThreshold: 30,
        onEnd: this.onDragEnd.bind(this)
      })
    )
  }

  async onDragEnd(event) {
    const dealId = event.item.dataset.dealId
    const stageId = event.to.dataset.stageId
    const isWon = event.to.dataset.stageWon === "true"
    const isLost = event.to.dataset.stageLost === "true"
    const isAmountRequired = event.to.dataset.stageAmountRequired === "true"
    const dealHasAmount = event.item.dataset.dealHasAmount === "true"

    if (!dealId) return
    // Drop dans une colonne sans stage_id (ex: "Deals inactifs") → revert visuel.
    if (!stageId) {
      this.revert(event)
      return
    }
    if (event.from === event.to && event.oldIndex === event.newIndex) return

    const body = { stage_id: stageId }

    // B2B : redirect vers la show page pour ouvrir la modale won/lost dédiée.
    // B2C : pas de modale won/lost → on tente la transition via API, la validation
    // serveur (amount / training_interest / lost_reason) renverra l'erreur en JSON
    // et le JS affiche un toast + revert.
    const hasDedicatedModal = this.resourcePathValue === "/deals"

    // B2B : on ouvre la modale won/lost dans le turbo-frame "modal" de la page
    // pipeline pour que "Annuler" laisse l'utilisateur sur le kanban au lieu
    // de le renvoyer sur la show du deal.
    if ((isLost || isWon) && hasDedicatedModal) {
      this.revert(event)
      const formPath = isWon ? "won_form" : "lost_form"
      const frame = document.getElementById("modal")
      if (frame) {
        frame.src = `${this.resourcePathValue}/${dealId}/${formPath}`
      } else {
        window.location.href = `${this.resourcePathValue}/${dealId}?open_${formPath}=true`
      }
      return
    }

    // Drop sur R2/CLOSING sans amount → modale amount_form. Symétrique des
    // modales won/lost. Évite que le sales ait un toast d'erreur fugace qu'il
    // ne voit pas (cas Martin ECLAT 2000, 28/05). Couvre aussi le reopen
    // PERDU→CLOSING où amount peut être nil.
    if (isAmountRequired && !dealHasAmount && hasDedicatedModal) {
      this.revert(event)
      const frame = document.getElementById("modal")
      if (frame) {
        frame.src = `${this.resourcePathValue}/${dealId}/amount_form?stage_id=${stageId}`
      } else {
        window.location.href = `${this.resourcePathValue}/${dealId}?open_amount_form=true&stage_id=${stageId}`
      }
      return
    }

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`${this.resourcePathValue}/${dealId}/change_stage`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })
      const result = await response.json()

      if (result.success) {
        showToast("Étape mise à jour")
      } else {
        this.revert(event)
        showToast(result.errors?.join(", ") || "Erreur lors du changement d'étape", "error")
      }
    } catch {
      this.revert(event)
      showToast("Erreur réseau", "error")
    }
  }

  async handleWonConfirmation(dealId, body) {
    try {
      const data = await fetchWonData(dealId)

      if (!data.needs_parent_check) {
        return confirm("Marquer comme gagné ?")
      }

      const result = await showWonModal(data.account_name, data.root_accounts)
      if (!result.confirmed) return false

      if (result.parentAccountId) {
        body.parent_account_id = result.parentAccountId
      }
      return true
    } catch {
      return confirm("Marquer comme gagné ?")
    }
  }

  revert(event) {
    if (event.from !== event.to) {
      event.from.insertBefore(event.item, event.from.children[event.oldIndex] || null)
    }
  }
}
